import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { propertyApi, type PropertyDto, type CreatePropertyInput } from "../api.js";
import { Button, Card, EmptyState, ErrorBanner, Field, Money, PageHeader, Spinner, TextInput } from "../components/ui.js";

export function PropertiesPage() {
  const [items, setItems] = useState<PropertyDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const res = await propertyApi.list();
    if (!res.ok) setError(res.error?.errorCode ?? "Failed to load");
    else setItems(res.data ?? []);
  };
  useEffect(() => { void load(); }, []);

  if (error) return <ErrorBanner message={error} />;
  if (items === null) return <Spinner />;
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Portfolio"
        title="Properties"
        description="Keep the essential details for every rental in one organised place."
        actions={<Link to="/properties/new" className="btn btn-primary">Add property</Link>}
      />
      {items.length === 0
        ? <EmptyState
            title="No properties yet"
            message="Add your first rental property to begin tracking income, expenses and portfolio performance."
            action={<Link to="/properties/new" className="btn btn-primary">Add your first property</Link>}
          />
        : <div className="prop-cards">
            {items.map((p) => (
              <Link to={`/properties/${p.id}`} key={p.id} className="prop-card">
                <h3>{p.name}</h3>
                <p className="muted">{p.fullAddress}</p>
                <p><span className="badge">{p.status}</span> <Money amount={p.configuredMonthlyRent ?? 0} currency={p.currency} /></p>
              </Link>
            ))}
          </div>}
    </div>
  );
}

export function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [prop, setProp] = useState<PropertyDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      const res = await propertyApi.get(id);
      if (!res.ok) setError(res.error?.errorCode ?? "Not found");
      else setProp(res.data ?? null);
    })();
  }, [id]);

  if (error) return <ErrorBanner message={error} />;
  if (!prop) return <Spinner />;
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Property"
        title={prop.name}
        description={prop.fullAddress}
        actions={<Link to={`/properties/${prop.id}/edit`} className="btn btn-secondary">Edit property</Link>}
      />
      <Card title="Property details" description="The current information used across your Nettstay workspace.">
        <dl className="summary-grid">
          <dt>Address</dt><dd>{prop.fullAddress}</dd>
          <dt>Status</dt><dd><span className="badge">{prop.status}</span></dd>
          <dt>Business use</dt><dd>{prop.businessUsePercent}%</dd>
          <dt>Monthly rent</dt><dd>{prop.configuredMonthlyRent === null ? <span className="muted">Not set</span> : <Money amount={prop.configuredMonthlyRent} currency={prop.currency} />}</dd>
          <dt>Country</dt><dd>{prop.countryCode}</dd>
        </dl>
      </Card>
    </div>
  );
}

const empty: CreatePropertyInput = { name: "", addressLine: "", suburb: "", city: "", postalCode: "", countryCode: "NZ", businessUsePercent: 100, configuredMonthlyRent: undefined, currency: "NZD", description: "" };

export function PropertyFormPage({ mode }: { mode: "create" | "edit" }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<CreatePropertyInput>(empty);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof CreatePropertyInput) => (e: { target: { value: string } }) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, [k]: k === "businessUsePercent" || k === "configuredMonthlyRent" ? (val === "" ? undefined : Number(val)) : val }));
  };

  useEffect(() => {
    if (mode !== "edit" || !id) return;
    void (async () => {
      const res = await propertyApi.get(id);
      if (res.ok && res.data) {
        setForm({ name: res.data.name, addressLine: res.data.fullAddress, suburb: "", city: "", postalCode: "", countryCode: res.data.countryCode, businessUsePercent: res.data.businessUsePercent, configuredMonthlyRent: res.data.configuredMonthlyRent ?? undefined, currency: res.data.currency, description: res.data.description ?? "" });
      }
    })();
  }, [mode, id]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const res = mode === "create" ? await propertyApi.create(form) : await propertyApi.update(id!, form);
    setBusy(false);
    if (!res.ok) { setError(res.error?.errorCode ?? "Save failed"); return; }
    navigate("/properties");
  };

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Portfolio"
        title={mode === "create" ? "Add a property" : "Edit property"}
        description="Keep the details concise and accurate. You can update them whenever your property changes."
      />
      <Card title="Property information">
        <form onSubmit={onSubmit} className="form-stack">
          {error && <ErrorBanner message={error} />}
          <Field label="Property name"><TextInput required value={form.name} onChange={set("name")} placeholder="For example, Harbour Apartment" /></Field>
          <Field label="Address line"><TextInput required value={form.addressLine} onChange={set("addressLine")} autoComplete="address-line1" /></Field>
          <Field label="Suburb"><TextInput value={form.suburb ?? ""} onChange={set("suburb")} autoComplete="address-level3" /></Field>
          <Field label="City"><TextInput value={form.city ?? ""} onChange={set("city")} autoComplete="address-level2" /></Field>
          <Field label="Postal code"><TextInput value={form.postalCode ?? ""} onChange={set("postalCode")} autoComplete="postal-code" /></Field>
          <Field label="Country code"><TextInput required value={form.countryCode} onChange={set("countryCode")} maxLength={2} /></Field>
          <Field label="Business use percentage"><TextInput type="number" min={0} max={100} required value={String(form.businessUsePercent)} onChange={set("businessUsePercent")} /></Field>
          <Field label="Configured monthly rent"><TextInput type="number" min={0} step="0.01" value={form.configuredMonthlyRent ?? ""} onChange={set("configuredMonthlyRent")} /></Field>
          <Field label="Currency"><TextInput required value={form.currency} onChange={set("currency")} maxLength={3} /></Field>
          <Field label="Description"><TextInput value={form.description ?? ""} onChange={set("description")} /></Field>
          <Button type="submit" disabled={busy}>{busy ? "Saving property…" : "Save property"}</Button>
        </form>
      </Card>
    </div>
  );
}
